import { useState } from 'react';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Instagram, Upload, Calculator } from 'lucide-react';

const CustomCakes = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [weight, setWeight] = useState(1);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    eventDate: '',
    designNotes: '',
    referenceImage: null
  });

  const cakeCategories = {
    regular: {
      name: 'Regular',
      basePrice: 1500,
      flavors: ['Vanilla', 'Choco Vanilla']
    },
    premium: {
      name: 'Premium',
      basePrice: 1600,
      flavors: ['Pineapple', 'Blackforest', 'Strawberry', 'Butterscotch', 'Rainbow', 'Mango', 'Mocha', 'Regular Chocolate']
    },
    exotic: {
      name: 'Exotic',
      basePrice: 1700,
      flavors: ['Mix Fruits', 'Rasmalai', 'Red Velvet', 'Blueberry', 'Chocolate Orange']
    },
    artisanal: {
      name: 'Artisanal',
      basePrice: 1800,
      flavors: ['Nutella Hazelnut', 'Lemon Blueberry', 'Orange Cranberry', 'Fig & Walnut']
    },
    luxury: {
      name: 'Luxury',
      basePrice: 2200,
      flavors: ['Pure Chocolate', 'Fully Fondant Cakes']
    }
  };

  const calculatePrice = () => {
    if (!selectedCategory || !weight) {
      toast.error('Please select category and weight');
      return;
    }
    const category = cakeCategories[selectedCategory];
    const basePrice = category.basePrice * weight;
    setCalculatedPrice(basePrice);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setFormData({ ...formData, referenceImage: file });
      toast.success('Reference image uploaded!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.eventDate) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!selectedCategory || !selectedFlavor) {
      toast.error('Please select cake category and flavor');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/custom-cakes/inquiry`,
        {
          name: formData.name,
          phone: formData.phone,
          event_date: formData.eventDate,
          category: selectedCategory,
          flavor: selectedFlavor,
          weight: weight,
          design_notes: formData.designNotes,
          calculated_price: calculatedPrice
        }
      );
      
      toast.success('Inquiry submitted successfully! We\'ll contact you soon.');
      
      // Open WhatsApp notification URL in new tab
      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
      
      // Reset form
      setFormData({ name: '', phone: '', eventDate: '', designNotes: '', referenceImage: null });
      setSelectedCategory('');
      setSelectedFlavor('');
      setWeight(1);
      setCalculatedPrice(null);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-accent mb-4" data-testid="custom-cakes-title">
              Custom Designer Cakes
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Create your dream cake! All our cakes are 100% eggless and made fresh with love.
            </p>
          </div>

          {/* Instagram Section */}
          <div className="mb-16">
            <Card className="p-8 bg-white">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Instagram className="h-8 w-8 text-primary" />
                  <h2 className="text-2xl font-bold text-accent">Follow Us on Instagram</h2>
                </div>
                <p className="text-muted-foreground mb-4">
                  Get inspired by our latest cake creations and customer favorites!
                </p>
                <a
                  href="https://www.instagram.com/hhouse_of_breadss/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-link"
                >
                  <Button className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transition-all">
                    <Instagram className="mr-2 h-5 w-5" />
                    @hhouse_of_breadss
                  </Button>
                </a>
              </div>
              
              {/* Horizontal Scrollable Instagram Feed */}
              <div className="relative group">
                <div className="flex overflow-x-auto gap-3 sm:gap-4 pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {/* Instagram-style Post Cards */}
                  <a 
                    href="https://www.instagram.com/hhouse_of_breadss/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-64 sm:w-80 snap-center group/card"
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-pink-100 via-purple-100 to-amber-100 aspect-square">
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-6 text-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 sm:p-6 mb-3 sm:mb-4 group-hover/card:scale-110 transition-transform">
                          <Instagram className="h-10 w-10 sm:h-16 sm:w-16 text-pink-600" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-bold text-accent mb-2">Designer Cakes</h3>
                        <p className="text-sm sm:text-base text-muted-foreground">Tap to see our latest creations</p>
                      </div>
                    </div>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hhouse_of_breadss/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-64 sm:w-80 snap-center group/card"
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-amber-100 via-orange-100 to-yellow-100 aspect-square">
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 mb-4 group-hover/card:scale-110 transition-transform">
                          <Instagram className="h-16 w-16 text-orange-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-accent mb-2">Custom Themes</h3>
                        <p className="text-muted-foreground">Birthday, Anniversary & More</p>
                      </div>
                    </div>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hhouse_of_breadss/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-80 snap-center group/card"
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-rose-100 via-red-100 to-pink-100 aspect-square">
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 mb-4 group-hover/card:scale-110 transition-transform">
                          <Instagram className="h-16 w-16 text-rose-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-accent mb-2">Premium Flavors</h3>
                        <p className="text-muted-foreground">Exotic & Luxury Options</p>
                      </div>
                    </div>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hhouse_of_breadss/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-80 snap-center group/card"
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-purple-100 via-indigo-100 to-blue-100 aspect-square">
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 mb-4 group-hover/card:scale-110 transition-transform">
                          <Instagram className="h-16 w-16 text-purple-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-accent mb-2">Fondant Art</h3>
                        <p className="text-muted-foreground">Beautiful Edible Designs</p>
                      </div>
                    </div>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hhouse_of_breadss/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-80 snap-center group/card"
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-green-100 via-emerald-100 to-teal-100 aspect-square">
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 mb-4 group-hover/card:scale-110 transition-transform">
                          <Instagram className="h-16 w-16 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-accent mb-2">100% Eggless</h3>
                        <p className="text-muted-foreground">Fresh & Healthy Bakes</p>
                      </div>
                    </div>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hhouse_of_breadss/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-80 snap-center group/card"
                  >
                    <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-yellow-100 via-amber-100 to-orange-100 aspect-square">
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 mb-4 group-hover/card:scale-110 transition-transform">
                          <Instagram className="h-16 w-16 text-amber-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-accent mb-2">Customer Reviews</h3>
                        <p className="text-muted-foreground">5★ Rated Creations</p>
                      </div>
                    </div>
                  </a>
                </div>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-full bg-gradient-to-r from-white to-transparent pointer-events-none"></div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-full bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  ← Scroll to explore • Click any card to visit Instagram →
                </p>
              </div>
            </Card>
          </div>

          {/* Cake Rate List */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-accent mb-6 text-center" data-testid="rate-list-title">
              Cake Menu & Pricing
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(cakeCategories).map(([key, category]) => (
                <Card key={key} className="p-6 hover:shadow-xl transition-shadow" data-testid={`category-card-${key}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-accent">{category.name}</h3>
                    <Badge className="bg-primary text-white text-lg px-3 py-1">
                      ₹{category.basePrice}+
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">per kg (base price)</p>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-accent">Flavors:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {category.flavors.map((flavor, idx) => (
                        <li key={idx} className="flex items-center">
                          <span className="mr-2 text-primary">•</span>
                          {flavor}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ))}
            </div>
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <p className="text-accent font-semibold mb-2">Important Note:</p>
              <p className="text-sm text-muted-foreground">
                Final cake price is always as per design & decoration. All bakes are made fresh & eggless.
                <br />Order confirmation on 100% advance • Delivery on actuals, when intimated in advance
              </p>
            </div>
          </div>

          {/* Price Calculator & Order Form */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calculator */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <Calculator className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold text-accent">Price Calculator</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="category">Select Category *</Label>
                  <Select onValueChange={setSelectedCategory} value={selectedCategory}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Choose category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(cakeCategories).map(([key, category]) => (
                        <SelectItem key={key} value={key}>
                          {category.name} - ₹{category.basePrice}/kg
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="flavor">Select Flavor *</Label>
                  <Select 
                    onValueChange={setSelectedFlavor} 
                    value={selectedFlavor}
                    disabled={!selectedCategory}
                  >
                    <SelectTrigger data-testid="flavor-select">
                      <SelectValue placeholder="Choose flavor" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCategory && cakeCategories[selectedCategory].flavors.map((flavor) => (
                        <SelectItem key={flavor} value={flavor}>
                          {flavor}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="weight">Cake Weight (KG) *</Label>
                  <Input
                    id="weight"
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={weight}
                    onChange={(e) => setWeight(parseFloat(e.target.value))}
                    data-testid="weight-input"
                  />
                </div>

                <Button
                  onClick={calculatePrice}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
                  data-testid="calculate-button"
                >
                  Calculate Price
                </Button>

                {calculatedPrice && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-primary rounded-xl p-6 text-center" data-testid="calculated-price">
                    <p className="text-sm text-muted-foreground mb-2">Tentative Base Price</p>
                    <p className="text-4xl font-bold text-primary">₹{calculatedPrice}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      *Final price varies based on design & decoration
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Order Form */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-accent mb-6">Place Your Order</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="order-form">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="+91XXXXXXXXXX"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="eventDate">Event Date *</Label>
                  <Input
                    id="eventDate"
                    type="date"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                    required
                    data-testid="event-date-input"
                  />
                </div>

                <div>
                  <Label htmlFor="designNotes">Design Notes (Optional)</Label>
                  <Textarea
                    id="designNotes"
                    value={formData.designNotes}
                    onChange={(e) => setFormData({ ...formData, designNotes: e.target.value })}
                    placeholder="Describe your cake design preferences..."
                    rows={3}
                    data-testid="design-notes-input"
                  />
                </div>

                <div>
                  <Label htmlFor="referenceImage">Upload Reference Design (Optional)</Label>
                  <div className="mt-2">
                    <label
                      htmlFor="referenceImage"
                      className="flex items-center justify-center w-full h-32 border-2 border-dashed border-amber-300 rounded-xl cursor-pointer hover:border-primary transition-colors"
                    >
                      {formData.referenceImage ? (
                        <div className="text-center">
                          <p className="text-sm text-accent font-semibold">{formData.referenceImage.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">Click to change</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Click to upload image</p>
                          <p className="text-xs text-muted-foreground">Max 5MB</p>
                        </div>
                      )}
                    </label>
                    <input
                      id="referenceImage"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="reference-image-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium transition-all hover:scale-105 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-inquiry-button"
                >
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  We&apos;ll contact you within 24 hours to discuss your custom cake design and finalize the order.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CustomCakes;